import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Grid,
  CircularProgress,
  Alert,
  Box,
  Typography,
  Autocomplete,
  Chip
} from '@mui/material';
import { createClinic, updateClinic, getAppointmentTypes } from './clinicService';

interface Clinic {
  id: number;
  name: string;
  business_id: string;
  practitioner_id: string;
  booking_url_path: string | null;
  logo_url: string | null;
  primary_color: string;
  allowed_appointment_types: string[];
  is_active: boolean;
  timezone: string;
}

interface AppointmentType {
  id: string;
  name: string;
}

interface ClinicDialogProps {
  open: boolean;
  onClose: (refreshNeeded: boolean) => void;
  clinic: Clinic | null;
}

const ClinicDialog: React.FC<ClinicDialogProps> = ({ open, onClose, clinic }) => {
  const [formData, setFormData] = useState<Partial<Clinic>>({
    name: '',
    business_id: '',
    practitioner_id: '',
    booking_url_path: '',
    logo_url: '',
    primary_color: '#3f51b5',
    allowed_appointment_types: [],
    is_active: true,
    timezone: 'Australia/Adelaide'
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appointmentTypes, setAppointmentTypes] = useState<AppointmentType[]>([]);
  const [appointmentTypesLoading, setAppointmentTypesLoading] = useState(false);
  
  // Load clinic data into form when editing
  useEffect(() => {
    if (clinic) {
      setFormData({
        ...clinic,
        booking_url_path: clinic.booking_url_path || '',
        logo_url: clinic.logo_url || ''
      });
    } else {
      // Reset form for new clinic
      setFormData({
        name: '',
        business_id: '',
        practitioner_id: '',
        booking_url_path: '',
        logo_url: '',
        primary_color: '#3f51b5',
        allowed_appointment_types: [],
        is_active: true
      });
    }
  }, [clinic]);
  
  // Load appointment types
  useEffect(() => {
    const fetchAppointmentTypes = async () => {
      try {
        setAppointmentTypesLoading(true);
        const data = await getAppointmentTypes();
        setAppointmentTypes(data.appointment_types || []);
      } catch (err: any) {
        console.error('Error loading appointment types:', err);
      } finally {
        setAppointmentTypesLoading(false);
      }
    };
    
    fetchAppointmentTypes();
  }, []);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'is_active' ? checked : value
    }));
  };
  
  const handleSubmit = async () => {
    // Validate form
    if (!formData.name || !formData.business_id || !formData.practitioner_id) {
      setError('Name, Business ID, and Practitioner ID are required');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      if (clinic) {
        // Update existing clinic
        await updateClinic(clinic.id, formData as Clinic);
      } else {
        // Create new clinic
        await createClinic(formData as Clinic);
      }
      
      onClose(true); // Close with refresh needed
    } catch (err: any) {
      setError(err.message || 'Failed to save clinic');
      console.error('Error saving clinic:', err);
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth>
      <DialogTitle>
        {clinic ? `Edit Clinic: ${clinic.name}` : 'Add New Clinic'}
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              name="name"
              label="Clinic Name"
              value={formData.name}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="business_id"
              label="Cliniko Business ID"
              value={formData.business_id}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
              helperText="From your Cliniko admin panel"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="practitioner_id"
              label="Default Practitioner ID"
              value={formData.practitioner_id}
              onChange={handleChange}
              fullWidth
              required
              disabled={loading}
              helperText="From your Cliniko practitioner profile"
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="booking_url_path"
              label="Booking URL Path"
              value={formData.booking_url_path}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              helperText={`Will be accessible at /booking/${formData.booking_url_path || '[clinic-path]'}`}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              name="primary_color"
              label="Primary Color"
              value={formData.primary_color}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              type="color"
              helperText="Brand color for the booking page"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              name="logo_url"
              label="Logo URL"
              value={formData.logo_url}
              onChange={handleChange}
              fullWidth
              disabled={loading}
              helperText="URL to the clinic's logo image"
            />
          </Grid>
          
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Allowed Appointment Types
            </Typography>
            <Autocomplete
              multiple
              options={appointmentTypes}
              getOptionLabel={(option) => `${option.name} (${option.id})`}
              value={appointmentTypes.filter(type => 
                formData.allowed_appointment_types?.includes(type.id)
              )}
              onChange={(_, newValue) => {
                setFormData(prev => ({
                  ...prev,
                  allowed_appointment_types: newValue.map(v => v.id)
                }));
              }}
              loading={appointmentTypesLoading}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option.id}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Select appointment types"
                  helperText="Leave empty to allow all types"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <React.Fragment>
                        {appointmentTypesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </React.Fragment>
                    ),
                  }}
                />
              )}
              fullWidth
              disabled={loading}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="timezone-label">Timezone</InputLabel>
              <Select
                labelId="timezone-label"
                name="timezone"
                value={formData.timezone || 'Australia/Adelaide'}
                onChange={handleChange as any}
                label="Timezone"
              >
                <MenuItem value="Australia/Adelaide">Adelaide (ACST/ACDT)</MenuItem>
                <MenuItem value="Australia/Sydney">Sydney/Melbourne (AEST/AEDT)</MenuItem>
                <MenuItem value="Australia/Brisbane">Brisbane (AEST)</MenuItem>
                <MenuItem value="Australia/Perth">Perth (AWST)</MenuItem>
                <MenuItem value="Australia/Darwin">Darwin (ACST)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  disabled={loading}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={() => onClose(false)} 
          disabled={loading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {clinic ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClinicDialog;